import { useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AsyncBoundary, Drawer, Icon, useToast } from '@kollez/design-system';
import { apiGet, apiPost, apiDelete } from '@/lib/api';

interface Project {
  _id?: string;
  title: string;
  description?: string;
  technologies?: string[];
  githubUrl?: string;
  liveUrl?: string;
  startDate?: string;
  endDate?: string;
}

/**
 * /projects — student projects.
 *
 * Deep-audit fixes (Page 9/N):
 *   - `plus` and `github` icons aren't in the registry — the Add button
 *     and GitHub link both rendered broken slots. Now use `folder-check`
 *     for Add and `folder-git-2` for GitHub (both registered).
 *   - Killed fabricated marketing claims ("triples interview conversion",
 *     "40% stronger") — no source, violated no-fabricated-aggregates.
 *   - Save failures were swallowed silently — drawer now stays open on
 *     error with an inline banner so the user can retry.
 *   - Surface schema caps (title 200, description 2000, tech ≤30) in the
 *     UI so the user sees the limit before hitting save-side rejection.
 *   - "– present" affordance when a project has startDate but no endDate.
 */
export function StudentProjectsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Project>({ title: '', description: '', technologies: [] });
  const [saveError, setSaveError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['student', 'me', 'projects'],
    queryFn: () => apiGet<{ rows: Project[] }>('/students/me/projects'),
  });

  const add = useMutation({
    mutationFn: (input: Project) => apiPost('/students/me/projects', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['student', 'me', 'projects'] });
      qc.invalidateQueries({ queryKey: ['student', 'me', 'dashboard'] });
      setOpen(false);
      setDraft({ title: '', description: '', technologies: [] });
      setSaveError(null);
    },
    onError: (e) => setSaveError((e as Error)?.message ?? 'Save failed'),
  });
  const del = useMutation({
    mutationFn: (id: string) => apiDelete(`/students/me/projects/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['student', 'me', 'projects'] }),
  });

  const projects = query.data?.rows ?? [];
  const descChars = (draft.description ?? '').length;
  const techCount = (draft.technologies ?? []).length;

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">My profile</p>
          <h1 className="h-page">Projects</h1>
          <p className="subhead">
            What you've built. Add a public GitHub link and a one-line description recruiters can scan.
            {projects.length > 0 && ` · ${projects.length} project${projects.length === 1 ? '' : 's'}`}
          </p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-primary" onClick={() => { setSaveError(null); setOpen(true); }}>
            <Icon name="folder-check" size={14} />Add project
          </button>
        </div>
      </div>

      <AsyncBoundary
        kind="cards"
        loading={query.isLoading}
        error={query.error}
        empty={projects.length === 0}
        emptyTitle="No projects added yet"
        emptyHint="Add one project with a GitHub link and a short description. Recruiters skim this — a real repo link is what moves you forward."
      >
        <div className="grid gap-4 md:grid-cols-2">
          {projects.map((p) => (
            <div key={p._id ?? p.title} className="card card-pad grid gap-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="cell-title">{p.title}</h3>
                  {p.startDate && (
                    <p className="cell-sub">
                      {new Date(p.startDate).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
                      {p.endDate
                        ? ` – ${new Date(p.endDate).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`
                        : ' – present'}
                    </p>
                  )}
                </div>
                {p._id && (
                  <button
                    className="btn btn-ghost btn-icon btn-sm"
                    aria-label={`Remove ${p.title}`}
                    onClick={() => del.mutate(p._id!, { onSuccess: () => toast.show({ message: `Removed "${p.title}".` }) })}
                  >
                    <Icon name="trash-2" size={12} />
                  </button>
                )}
              </div>
              {p.description && <p className="text-sm">{p.description}</p>}
              {p.technologies && p.technologies.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {p.technologies.map((t) => <span key={t} className="qtype">{t}</span>)}
                </div>
              )}
              <div className="flex gap-3 mt-1 text-sm">
                {p.githubUrl && (
                  <a className="link inline-flex items-center gap-1" href={p.githubUrl} target="_blank" rel="noopener noreferrer">
                    <Icon name="folder-git-2" size={12} />GitHub
                  </a>
                )}
                {p.liveUrl && (
                  <a className="link inline-flex items-center gap-1" href={p.liveUrl} target="_blank" rel="noopener noreferrer">
                    <Icon name="external-link" size={12} />Live
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </AsyncBoundary>

      <Drawer open={open} onClose={() => setOpen(false)} title="Add project" size="md">
        <div className="grid gap-4">
          {saveError && (
            <div className="notice notice-warning" role="alert">
              {saveError}
            </div>
          )}
          <label className="grid gap-1.5">
            <span className="label">Title</span>
            <input
              className="input"
              value={draft.title}
              maxLength={200}
              onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="label">
              Description <span className="cell-sub">· {descChars}/2000</span>
            </span>
            <textarea
              className="textarea"
              rows={3}
              maxLength={2000}
              value={draft.description ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="label">
              Technologies (comma-separated) <span className="cell-sub">· {techCount}/30</span>
            </span>
            <input
              className="input"
              value={(draft.technologies ?? []).join(', ')}
              onChange={(e) => setDraft((d) => ({
                ...d,
                technologies: e.target.value.split(',').map((s) => s.trim()).filter(Boolean).slice(0, 30),
              }))}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="label">GitHub URL</span>
            <input
              className="input"
              type="url"
              placeholder="https://github.com/…"
              value={draft.githubUrl ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, githubUrl: e.target.value }))}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="label">Live URL (optional)</span>
            <input
              className="input"
              type="url"
              placeholder="https://…"
              value={draft.liveUrl ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, liveUrl: e.target.value }))}
            />
          </label>
        </div>
        <div className="drawer-foot">
          <button className="btn btn-outline" onClick={() => setOpen(false)}>Cancel</button>
          <button
            className="btn btn-primary ml-auto"
            disabled={add.isPending || draft.title.trim().length < 2}
            onClick={() => {
              setSaveError(null);
              add.mutate(draft, {
                onSuccess: () => toast.show({ message: `Added "${draft.title}".` }),
              });
            }}
          >
            {add.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </Drawer>
    </div>
  );
}
